if (!customElements.get('sa-contact-form')) {
  class SaContactForm extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.syncCountrySelectDisplay = this.syncCountrySelectDisplay.bind(this);
    }

    connectedCallback() {
      // Use event delegation with CAPTURE phase
      this.addEventListener('submit', this.handleSubmit, true);

      // Cache message elements
      this.popupMessage = this.querySelector('.sa-popup-message');
      
      // Hide messages on load (in case browser cached the state)
      this.hideMessages();
      this.setupCountrySelectDisplay();

      // Real-time validation: Clear error state on input interaction
      this.querySelectorAll('input, select, textarea').forEach(input => {
        ['input', 'change'].forEach(evt => {
          input.addEventListener(evt, () => {
            const wrapper = input.closest('.sa-contact-form__field');
            if (wrapper) wrapper.classList.remove('sa-contact-form__field--error');
          });
        });
      });
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.handleSubmit, true);
      if (this.countrySelect) {
        this.countrySelect.removeEventListener('input', this.syncCountrySelectDisplay);
        this.countrySelect.removeEventListener('change', this.syncCountrySelectDisplay);
      }
    }

    setupCountrySelectDisplay() {
      const countrySelect = this.querySelector('#ContactForm-country');
      if (!countrySelect) return;

      const field = countrySelect.closest('.sa-contact-form__field--country');
      if (!field) return;

      let display = field.querySelector('.sa-contact-form__country-display');
      if (!display) {
        display = document.createElement('span');
        display.className = 'sa-contact-form__country-display';
        display.setAttribute('aria-hidden', 'true');
        countrySelect.insertAdjacentElement('afterend', display);
      }

      this.countrySelect = countrySelect;
      this.countryDisplay = display;
      field.dataset.shortCountryReady = 'true';
      countrySelect.addEventListener('input', this.syncCountrySelectDisplay);
      countrySelect.addEventListener('change', this.syncCountrySelectDisplay);
      this.syncCountrySelectDisplay();
    }

    syncCountrySelectDisplay() {
      if (!this.countrySelect || !this.countryDisplay) return;

      const selected = this.countrySelect.options[this.countrySelect.selectedIndex];
      const label = selected ? selected.textContent.replace(/\s*\([^)]*\)\s*$/, '').trim() : '';
      this.countryDisplay.textContent = label;
    }

    validateForm() {
      let isValid = true;
      let firstInvalid = null;

      const fields = this.querySelectorAll('.sa-contact-form__field');
      
      fields.forEach(field => {
        const input = field.querySelector('input[required], select[required], textarea[required]');
        
        let isFieldValid = true;
        
        if (input) {
           // Standard check for text/email/tel/select
           const val = input.value.trim();
           
           if (!val) {
             isFieldValid = false;
             // Restore default error message
             const errorText = field.querySelector('.sa-contact-form__error-text');
             if (errorText) errorText.textContent = 'This field is required';
           } else if (input.type === 'email') {
             // Email format validation
             const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
             if (!emailRegex.test(val)) {
               isFieldValid = false;
               // Set custom error message for invalid email
               const errorText = field.querySelector('.sa-contact-form__error-text');
               if (errorText) errorText.textContent = 'Please enter a valid email address';
             }
           }
        } else {
            // Optional field or no required input found
            return;
        }

        if (!isFieldValid) {
            isValid = false;
            field.classList.add('sa-contact-form__field--error');
            if (!firstInvalid) firstInvalid = field;
        } else {
            field.classList.remove('sa-contact-form__field--error');
        }
      });
      
      // Check Terms Checkbox
      const terms = this.querySelector('#ContactForm-terms');
      if (terms && !terms.checked) {
          isValid = false;
          if (!firstInvalid) {
             this.showError('Please agree to the terms and conditions.');
             return false;
          }
      }

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = firstInvalid.querySelector('input:not([type="hidden"]), select, textarea');
        if (input) input.focus();
        return false;
      }

      return isValid;
    }

    async getIpInfo() {
      let ipInfo = {}
      try {
        ipInfo = await fetch('https://ipapi.co/json/').then(r => r.json())
      } catch(e) {
        console.log(e)
      }
      return ipInfo;
    }

    async handleSubmit(e) {
      e.preventDefault();
      e.stopPropagation(); 
      e.stopImmediatePropagation();
      
      const submitBtn = this.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('loading');
        const originalText = submitBtn.innerHTML;
        submitBtn.innerHTML = 'Sending...';
        submitBtn.disabled = true;
        this.submitBtn = submitBtn;
        this.originalBtnText = originalText;
      }

      // Hide previous messages
      this.hideMessages();

      // Validation
      if (!this.validateForm()) {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.innerHTML = this.originalBtnText;
          submitBtn.disabled = false;
        }
        return;
      }

      // Collect data
      const formData = new URLSearchParams();
      const getValue = (selector) => {
        const el = this.querySelector(selector);
        const val = el && el.value ? el.value.trim() : '';
        return val === '' ? ' ' : val;
      };

      formData.append('firstName', getValue('[name="contact[first_name]"]'));
      formData.append('lastName', getValue('[name="contact[last_name]"]'));
      formData.append('email', getValue('[name="contact[email]"]'));
      
      const country = getValue('[name="contact[country]"]');
      const phone = getValue('[name="contact[phone]"]');
      const combinedPhone = (country + ' ' + phone).trim();
      formData.append('phone', combinedPhone === '' ? ' ' : combinedPhone);
      
      formData.append('organization', getValue('[name="contact[organization]"]'));
      formData.append('jobTitle', getValue('[name="contact[job_title]"]'));
      formData.append('message', getValue('[name="contact[body]"]'));

      const subscribeCheckbox = this.querySelector('[name="contact[subscribe]"]');
      formData.append('subscribe', subscribeCheckbox?.checked ? 'yes' : 'no');

      // Contact Form usually doesn't have Product Interest, so we can omit or send empty
      formData.append('type', ' '); 

      const ipInfo = await this.getIpInfo()
      if (ipInfo.ip) {
        formData.append('country_ip', ipInfo.ip);
      }
      if (ipInfo.country_name) {
        formData.append('country_name', ipInfo.country_name);
      }

      console.log('Submitting form data:', Object.fromEntries(formData));

      try {
        const response = await fetch('/apps/sharpa-contact/contact-submit?shop=sharpa-robot.myshopify.com', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData.toString()
        });

        if (response.ok) {
           this.showSuccess();
           const form = e.target;
           if (form && typeof form.reset === 'function') {
             form.reset();
             this.syncCountrySelectDisplay();
           } else {
             const foundForm = this.querySelector('form');
             if (foundForm) {
               foundForm.reset();
               this.syncCountrySelectDisplay();
             }
           }
        } else {
           throw new Error('Submission failed');
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        this.showError();
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.innerHTML = this.originalBtnText;
          submitBtn.disabled = false;
        }
      }
    }

    hideMessages() {
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
        this.messageTimeout = null;
      }
      if (this.popupMessage) this.popupMessage.style.display = 'none';
      
      // Also hide default liquid messages if they exist
      const defaultSuccess = this.querySelector('.sa-contact-form__success');
      const defaultError = this.querySelector('.sa-contact-form__error');
      if (defaultSuccess) defaultSuccess.style.display = 'none';
      if (defaultError) defaultError.style.display = 'none';
    }

    showSuccess() {
      this.hideMessages(); // Clear any existing messages/timeouts
      if (this.popupMessage) {
        this.popupMessage.textContent = 'We have received your application and will contact you as soon as possible.';
        this.popupMessage.style.backgroundColor = '#1a1a1a';
        this.popupMessage.style.display = 'block';
        // Auto hide after 3 seconds
        this.messageTimeout = setTimeout(() => {
          this.hideMessages();
        }, 3000);
      }
    }

    showError(message) {
      this.hideMessages(); // Clear any existing messages/timeouts
      if (this.popupMessage) {
        this.popupMessage.textContent = message || 'Submission failed. Please try again.';
        this.popupMessage.style.backgroundColor = '#E42F23';
        this.popupMessage.style.display = 'block';
        // Auto hide after 3 seconds
        this.messageTimeout = setTimeout(() => {
          this.hideMessages();
        }, 3000);
      }
    }
  }
  
  customElements.define('sa-contact-form', SaContactForm);
}
